import { useEffect, useRef } from 'react'
import { FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { InvisibleSmartCaptcha } from '@yandex/smart-captcha'
import { captchaAttemptAtom, captchaStatusAtom, resetCaptchaAtom } from 'components/auth/lib/atoms'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTheme } from 'lib/colors'
import { useInView } from 'react-intersection-observer'

const NO_RESPONSE_TIMEOUT_MS = 15_000
const CHALLENGE_CLOSED_TIMEOUT_MS = 2_000

type Props = {
  visible?: boolean
}

const Captcha: FC<Props> = ({ visible = true }) => {
  const ctx = useUserContext()
  const { effectiveTheme } = useTheme()
  const setCaptchaStatus = useSetAtom(captchaStatusAtom)
  const resetCaptcha = useSetAtom(resetCaptchaAtom)
  const attempt = useAtomValue(captchaAttemptAtom)
  const failTimer = useRef<ReturnType<typeof setTimeout>>()
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '300px' })

  const cancelFailTimer = () => clearTimeout(failTimer.current)

  const fail = () => {
    cancelFailTimer()
    setCaptchaStatus((status) =>
      status.passed ? status : { passed: false, token: null, failed: true }
    )
  }

  const failAfter = (ms: number) => {
    cancelFailTimer()
    failTimer.current = setTimeout(fail, ms)
  }

  useEffect(() => {
    if (visible) failAfter(NO_RESPONSE_TIMEOUT_MS)
    return cancelFailTimer
  }, [visible, attempt])

  useEffect(() => () => resetCaptcha(), [])

  return (
    <div ref={ref}>
      {(visible || inView) && (
        <InvisibleSmartCaptcha
          key={attempt}
          visible={visible}
          hideShield
          sitekey="ysc1_6EBUen0UmykzOre93t7vBko9lbLlGHG7F0a5GQRcb359a26c"
          onSuccess={(token) => {
            cancelFailTimer()
            setCaptchaStatus({ passed: true, token, failed: false })
          }}
          onChallengeVisible={cancelFailTimer}
          onChallengeHidden={() => failAfter(CHALLENGE_CLOSED_TIMEOUT_MS)}
          onJavascriptError={fail}
          onNetworkError={fail}
          onTokenExpired={resetCaptcha}
          theme={effectiveTheme as 'dark' | 'light'}
          language={ctx.lang === 'zh' ? 'en' : (ctx.lang as 'ru' | 'en' | 'uz')}
        />
      )}
    </div>
  )
}

export default Captcha
